import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import './BKButton.css';

/**
 * Standard button component for the application.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.children]
 * @param {'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'outline-primary' | 'outline-secondary' | 'outline-success' | 'outline-danger' | 'icon-edit' | 'icon-user' | 'icon-delete' | 'icon-move' | 'icon-tasks'} [props.variant='primary']
 * @param {'small' | 'medium' | 'large'} [props.size='medium']
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.loading=false]
 * @param {React.ReactNode} [props.icon=null]
 * @param {'start' | 'end'} [props.iconPosition='start']
 * @param {boolean} [props.fullWidth=false]
 * @param {(e: React.MouseEvent<HTMLElement>) => void} [props.onClick]
 * @param {string} [props.type='button']
 * @param {'button' | 'submit' | 'reset'} [props.htmlType='button']
 * @param {string} [props.bgColor]
 * @param {string} [props.textColor]
 * @param {import('react').HTMLAttributes<HTMLButtonElement>} [rest]
 */
const BKButton = ({
  children,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  icon = null,
  iconPosition = 'start',
  fullWidth = false,
  onClick,
  type = 'button',
  htmlType = 'button',
  bgColor,
  textColor,
  style,
  ...rest
}) => {
  const getVariantClass = () => {
    switch (variant) {
      case 'primary':
        return 'bk-button-primary';
      case 'secondary':
        return 'bk-button-secondary';
      case 'success':
        return 'bk-button-success';
      case 'danger':
        return 'bk-button-danger';
      case 'warning':
        return 'bk-button-warning';
      case 'info':
        return 'bk-button-info';
      case 'outline-primary':
        return 'bk-button-outline-primary';
      case 'outline-secondary':
        return 'bk-button-outline-secondary';
      case 'outline-success':
        return 'bk-button-outline-success';
      case 'outline-danger':
        return 'bk-button-outline-danger';
      case 'icon-edit':
        return 'bk-button-icon-edit';
      case 'icon-user':
        return 'bk-button-icon-user';
      case 'icon-delete':
        return 'bk-button-icon-delete';
      case 'icon-move':
        return 'bk-button-icon-move';
      case 'icon-tasks':
        return 'bk-button-icon-tasks';
      default:
        return 'bk-button-primary';
    }
  };

  const getSizeClass = () => {
    if (variant && variant.startsWith('icon-')) {
      return 'bk-button-icon';
    }
    switch (size) {
      case 'small':
        return 'bk-button-small';
      case 'medium':
        return 'bk-button-medium';
      case 'large':
        return 'bk-button-large';
      default:
        return 'bk-button-medium';
    }
  };

  const className = `bk-button ${getVariantClass()} ${getSizeClass()} ${
    fullWidth ? 'bk-button-full-width' : ''
  }`;

  return (
    <Button
      className={className}
      disabled={disabled}
      loading={loading}
      icon={iconPosition === 'start' ? icon : null}
      onClick={onClick}
      htmlType={htmlType}
      block={fullWidth}
      style={{
        ...style,
        ...(bgColor ? { backgroundColor: bgColor, borderColor: bgColor } : {}),
        ...(textColor ? { color: textColor } : {})
      }}
      {...rest}>
      {children}
      {iconPosition === 'end' && icon && (
        <span className="bk-button-icon-end">{icon}</span>
      )}
    </Button>
  );
};

BKButton.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'success',
    'danger',
    'warning',
    'info',
    'outline-primary',
    'outline-secondary',
    'outline-success',
    'outline-danger',
    'icon-edit',
    'icon-user',
    'icon-delete',
    'icon-move',
    'icon-tasks'
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(['start', 'end']),
  fullWidth: PropTypes.bool,
  onClick: PropTypes.func,
  type: PropTypes.string,
  htmlType: PropTypes.oneOf(['button', 'submit', 'reset']),
  bgColor: PropTypes.string,
  textColor: PropTypes.string
};

export default BKButton;
